import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  MenuItem,
  Select,
  Drawer,
  List,
  ListItem,
  ListItemText,
  TextField,
  Button,
  Divider,
} from "@mui/material";
import ledgerDetail from "./ledgerDetail.json";
import "./LedgerDetail.scss";
import Header from "../../../Home/Header/Header";
import dayjs from "dayjs";

const LedgerDetail = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [groupFilter, setGroupFilter] = useState("All Groups");

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [customDate, setCustomDate] = useState("");

  const selectedCompanyName = state?.companyName;
  const companyData = ledgerDetail.find(
    (c) => c.companyName === selectedCompanyName
  );

  const entries = companyData?.transactions || [];

  const handleEntryClick = (entry) => {
    navigate("/LedgerMoreDetail", {
      state: { entry, company: selectedCompanyName },
    });
  };

  const handleDateSelect = (option) => {
    setSelectedDate(option);
    if (option !== "Custom") {
      setDrawerOpen(false);
    }
  };

  const handleApplyCustomDate = () => {
    if (customDate) {
      setSelectedDate("Custom");
      setDrawerOpen(false);
    }
  };

  const resolveDateFilter = () => {
    if (selectedDate === "Today") return dayjs().format("YYYY-MM-DD");
    if (selectedDate === "Yesterday")
      return dayjs().subtract(1, "day").format("YYYY-MM-DD");
    if (selectedDate === "This Week") {
      return {
        range: [
          dayjs().startOf("week").format("YYYY-MM-DD"),
          dayjs().endOf("week").format("YYYY-MM-DD"),
        ],
      };
    }
    if (selectedDate === "This Month") {
      return {
        range: [
          dayjs().startOf("month").format("YYYY-MM-DD"),
          dayjs().endOf("month").format("YYYY-MM-DD"),
        ],
      };
    }
    if (selectedDate === "Custom" && customDate) return customDate;
    return null;
  };

  const filterByDate = (entries) => {
    const resolved = resolveDateFilter();
    if (!resolved) return entries;

    if (typeof resolved === "string") {
      return entries.filter((e) => e.date.includes(resolved));
    } else if (resolved.range) {
      const [start, end] = resolved.range;
      return entries.filter((e) => e.date >= start && e.date <= end);
    }
    return entries;
  };

  const filteredEntries = filterByDate(entries);

  return (
    <div className="ledger-detail-container">
      <Header otherPage={true} title={state?.companyName} />

      <div className="group-filter" style={{ paddingTop: "60px" }}>
        <Select
          fullWidth
          value={groupFilter}
          onChange={(e) => setGroupFilter(e.target.value)}
          className="gruped_dropDown_selection"
          sx={{
            outline: "none",
            "& .MuiSelect-select": {
              padding: "0px",
            },
          }}
        >
          <MenuItem value="All Groups">All Groups</MenuItem>
          <MenuItem value="B2B">B2B</MenuItem>
          <MenuItem value="B2C">B2C</MenuItem>
          <MenuItem value="Branch">Branch</MenuItem>
        </Select>
      </div>

      <div className="date-filter">
        <Button
          fullWidth
          variant="outlined"
          onClick={() => setDrawerOpen(true)}
          style={{ textTransform: "none" }}
          className="DatePcikerButton"
        >
          {selectedDate === "Custom" && customDate
            ? `Selected: ${customDate}`
            : selectedDate || "Select Date"}
        </Button>
      </div>

      <Drawer
        anchor="bottom"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          style: { borderTopLeftRadius: 12, borderTopRightRadius: 12 },
        }}
      >
        <List>
          {["Today", "Yesterday", "This Week", "This Month", "Custom"].map(
            (option) => (
              <ListItem
                button
                key={option}
                onClick={() => handleDateSelect(option)}
                selected={selectedDate === option}
              >
                <ListItemText primary={option} />
              </ListItem>
            )
          )}
        </List>

        {selectedDate === "Custom" && (
          <>
            <Divider />
            <div style={{ padding: "16px" }}>
              <TextField
                type="date"
                fullWidth
                variant="outlined"
                value={customDate}
                onChange={(e) => setCustomDate(e.target.value)}
              />
              <Button
                variant="contained"
                fullWidth
                style={{ marginTop: 12 }}
                onClick={handleApplyCustomDate}
              >
                Apply
              </Button>
            </div>
          </>
        )}
      </Drawer>

      <div className="ledger-content">
        <div className="balance">
          Opening Balance:{" "}
          <span style={{ fontWeight: 600 }}>
            ₹{companyData?.openingBalance?.toLocaleString() || 0}
          </span>
        </div>

        <div className="entries">
          {filteredEntries.length > 0 ? (
            filteredEntries.map((entry, idx) => (
              <div
                className="entry-item"
                key={idx}
                onClick={() => handleEntryClick(entry)}
              >
                <div className="entry-top">
                  <span>{entry.date}</span>
                  <span>{entry.type}</span>
                </div>
                {entry.voucherNo && (
                  <div className="voucher">Voucher No: {entry.voucherNo}</div>
                )}
                <div className="entry-bottom">
                  <span className="amount">
                    ₹{entry.amount.toLocaleString()}
                  </span>
                  <span className="running">
                    (₹{entry.balance.toLocaleString()})
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="no_entries">No entries found.</div>
          )}
        </div>

        <div className="balance">
          Closing Balance:{" "}
          <span style={{ fontWeight: 600 }}>
            ₹{companyData?.closingBalance?.toLocaleString() || 0}
          </span>
        </div>
      </div>
    </div>
  );
};

export default LedgerDetail;
